import React, { InputHTMLAttributes } from 'react';
import { useFormContext, get } from 'react-hook-form';
import styled from 'styled-components';

import { getFormError } from 'constants/formErrors';

import { TotemLabel } from '../TotemLabel';
import { BasicInput } from '../BasicInput';

type DetailFormSmallValueProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'defaultValue'> & {
    label?: string;
    sublabel?: string;
    name?: string;
    $centerText?: boolean;
    defaultValue?: string | number | null;
    customError?: string;
    width?: string;
    height?: string;
};

export const DetailFormSmallValue = React.forwardRef<HTMLInputElement, DetailFormSmallValueProps>(
    (
        {
            label,
            sublabel,
            name = '',
            defaultValue,
            customError,
            placeholder,
            style,
            value,
            width = '40px',
            height = '30px',
            ...props
        },
        ref,
    ) => {
        const {
            formState: { errors },
        } = useFormContext();
        const errorObject = get(errors, name);
        const error = customError || (errorObject ? getFormError(errorObject.type, errorObject.message) : '');

        return (
            <Container style={style}>
                <LabelContainer $hasMarginBottom={Boolean(label || sublabel)}>
                    {label && <TotemLabel>{label}</TotemLabel>}
                    {sublabel && <Sublabel>{sublabel}</Sublabel>}
                </LabelContainer>
                <ValueContainer $width={width} $height={height}>
                    <BasicInput
                        name={name}
                        placeholder={placeholder || '0'}
                        defaultValue={defaultValue ?? ''}
                        width={width}
                        height={height}
                        ref={ref}
                        value={value}
                        style={{ padding: '2px 4px', ...style }}
                        {...props}
                    />
                    {error && <Error>{error}</Error>}
                </ValueContainer>
            </Container>
        );
    },
);

DetailFormSmallValue.displayName = 'DetailFormSmallValue';

const Error = styled.div`
    position: absolute;
    right: -2px;
    top: -16px;
    font-size: 12px;
    color: ${({ theme }) => theme.errorColor};
    width: max-content;
    white-space: nowrap;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => theme.textColor};
`;

const LabelContainer = styled.div<{ $hasMarginBottom: boolean }>`
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ $hasMarginBottom }) => ($hasMarginBottom ? 3 : 0)}px;
`;

const Sublabel = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 11px;
`;

const ValueContainer = styled.div<{ $width: string; $height: string }>`
    position: relative;
    width: ${({ $width }) => $width};
    height: ${({ $height }) => $height};
    min-width: 30px;
    min-height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
`;
